/**
 * Weiche Rückfrage bei ungewöhnlich hohem Gewicht.
 *
 * Ab RUECKFRAGE_AB_KG wird vor dem Speichern einmal nachgefragt — bestätigen,
 * nicht sperren: 98 legitime Einträge über 1.000 kg in sieben Monaten belegen,
 * dass solche Werte vorkommen (der größte echte: 5.665 kg). Die harte Grenze
 * gegen Stellen-Tippfehler sitzt im Backend (gewicht-grenze.ts, 10.000 kg) —
 * diese Rückfrage erspart nur die Korrektur-Arbeit danach.
 *
 * Bewusst ein In-App-Dialog (kein window.confirm) im Stil der übrigen
 * Stations-Dialoge; per Portal, damit nichts im Layout springt.
 */

import { createPortal } from 'react-dom';
import { Scale } from 'lucide-react';

export const RUECKFRAGE_AB_KG = 1000;

interface GewichtRueckfrageProps {
  /** Das zu bestätigende Gewicht in kg */
  kg: number;
  onBestaetigen: () => void;
  onZurueck: () => void;
}

export function GewichtRueckfrage({ kg, onBestaetigen, onZurueck }: GewichtRueckfrageProps) {
  const anzeige = kg.toLocaleString('de-DE', { maximumFractionDigits: 1 });

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Scale className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Ungewöhnlich hohes Gewicht</h3>
              <p className="text-sm text-gray-500">Bitte einmal prüfen</p>
            </div>
          </div>

          <p className="text-gray-700 mb-1">Diese Erfassung würde speichern:</p>
          <p className="text-4xl font-bold text-gray-900 tabular-nums mb-4">{anzeige} kg</p>
          <p className="text-sm text-gray-500 mb-6">
            Das ist mehr als {RUECKFRAGE_AB_KG.toLocaleString('de-DE')} kg. Wenn die Zahl stimmt,
            einfach bestätigen — wenn nicht, zurück und das Gewicht ändern.
          </p>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onZurueck}
              className="flex-1 px-4 py-3 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800"
            >
              Zurück und prüfen
            </button>
            <button
              type="button"
              onClick={onBestaetigen}
              className="flex-1 px-4 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
            >
              Ja, {anzeige} kg stimmen
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
